/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  `useEventAccess` — «что мне можно в ЭТОМ мероприятии»
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Единственная точка, которую спрашивают шаблоны внутри скоупа мероприятия.
 * Всё, что она делает, — отдаёт реактивные ссылки на геттеры `stores/event.ts`;
 * ценность в том, что вопрос задаётся **одинаково** во всех двенадцати местах,
 * где он возникает.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ТРИ СПОСОБА НАПИСАТЬ ЭТУ ПРОВЕРКУ НЕПРАВИЛЬНО
 * ───────────────────────────────────────────────────────────────────────────
 *
 * 1. **`myMemberRole === 'MEMBER_ROLE_OWNER'`** — прячет всё от администратора:
 *    он в составе мероприятия не числится вовсе, и роль у него честно `null`.
 * 2. **`isAdmin`** — прячет всё от владельца, ради которого мероприятия и
 *    заведены как скоуп.
 * 3. **Сравнение ролей по порядку** (`role <= OWNER`) — работало ровно до
 *    появления третьей роли.
 *
 * ⚠️ ТРЕТЬЯ РОЛЬ ПОЯВИЛАСЬ. Это `MEMBER_ROLE_VIEWER_PLUS` («Наблюдатель+»), и
 * пункт 3 из предупреждения на будущее стал описанием сегодняшнего дня. Номер
 * у неё **3**, потому что номера в proto — это провод, их не переупорядочивают:
 * `OWNER = 1`, `VIEWER = 2`, `VIEWER_PLUS = 3`. То есть по порядку она стоит
 * ПОСЛЕ обычного наблюдателя, хотя прав у неё больше, — и любое сравнение по
 * порядку теперь ошибается в обе стороны сразу: `role >= VIEWER_PLUS` не даёт
 * ей ничего лишнего только по случайности, а `role <= VIEWER` молча приравняло
 * бы её к наблюдателю. Проверено грепом на момент введения роли: сравнений по
 * порядку в коде нет ни одного, и появляться им негде — оба ответа считает
 * стор.
 *
 * Правильных ответов теперь два, и оба живут в сторе с ЯВНЫМ перечислением
 * ролей:
 *
 *   `canManage`          = isAdmin || роль === owner
 *                          «распоряжаюсь мероприятием»;
 *   `canEditScanFields`  = isAdmin || роль === owner || роль === viewer_plus
 *                          «могу писать значение графы, ПОДСВЕЧЕННОЙ для
 *                          сканирования».
 *
 * ⚠️ ВТОРОЙ — ЭТО НЕ «ОСЛАБЛЕННЫЙ ПЕРВЫЙ», И ОН НЕ ПОЛНЫЙ ОТВЕТ САМ ПО СЕБЕ.
 * Право «наблюдателя+» привязано к ПОЛЮ: писать можно только туда, где
 * `field.highlightOnScan`. Условие в UI обязано складываться из двух половин —
 * `canEditScanFields && field.highlightOnScan`, — ровно как на сервере в
 * `EventGuard.RequireScanFieldWrite`. Половина условия выдаёт «наблюдателю+»
 * все дополнительные поля мероприятия разом.
 *
 * ⚠️ И право отзывается не сменой роли, а снятием подсветки с поля. Значит,
 * открытый экран может получить 403 в любой момент — те, кто даёт править такие
 * графы, обязаны этот отказ показать словами и вернуть графу в режим чтения:
 * карточка (`ParticipantForm`) и галочка прямо в строке списка
 * (`ParticipantsTable` → `canToggle`, отказ разбирает `ParticipantsListView`).
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ `ready` — НЕ КОСМЕТИКА
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Пока состав не загружен, `canManage` равен `false` — не потому, что прав нет,
 * а потому что ответ не приехал. Кнопка, отрисованная по `canManage` без учёта
 * `ready`, у настоящего владельца моргает недоступной. Поэтому шаблоны, которым
 * важна эта разница, показывают скелетон по `!ready`, а не «недостаточно прав».
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  И ГЛАВНОЕ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Это **аффорданс, а не авторизация**. Скрытая кнопка ничего не защищает:
 * тот же запрос отправляется из консоли за десять секунд. Настоящая проверка —
 * `biz.EventGuard` на бэкенде, который отдаёт 403 наблюдателю и 404
 * не-участнику. Здесь мы всего лишь не рисуем заведомо отказные действия.
 */
import { storeToRefs } from 'pinia'

import { useEventStore } from '@/stores/event'

export function useEventAccess() {
  const store = useEventStore()

  const {
    event,
    eventId,
    eventName,
    members,
    owners,
    myMembership,
    myMemberRole,
    canView,
    canManage,
    canEditScanFields,
    loading,
    ready,
    error,
  } = storeToRefs(store)

  return {
    event,
    eventId,
    eventName,
    members,
    owners,
    myMembership,
    myMemberRole,
    canView,
    canManage,
    canEditScanFields,
    loading,
    ready,
    error,
    store,
  }
}
